import { AfterViewInit, DestroyRef, Directive, inject, Injector, OnDestroy, OnInit } from "@angular/core";
import { Observable } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { LayerService } from "../../../services/internal-services/layer.service";
import { GeoService } from "../../../services/internal-services/geo.service";
import { ViewerConfigurationService } from "../../../services/viewer-configuration.service";
import { ViewerErrorCommunicatorService } from "../../../services/communicators/viewer-error-communicator.service";
import { MapOverlaysService } from "../../../services/internal-services/overlay/map-overlays.service";
import { IPopup } from "../../../interfaces/i-popup";
import { MeasurementToolService } from "../../../services/internal-services/measurement-tool.service";
import { ISelectedFeatures } from "../../../interfaces/i-select-features";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { ImageGalleryService } from "../../../services/internal-services/image-gallery/image-gallery.service";
import { FeaturesSelectedService } from "../../../services/internal-services/features-selected.service";
import { LoadingService } from "../../../services/internal-services/loading.service";
import { IUrlParams } from "../../../interfaces/i-url-params";
import { Viewerlayer } from "../../../model/viewerlayer";
import { toolbarOptions } from "../../../../shared/model/toolbar-constants";
import { ToolbarService } from "../../../services/internal-services/toolbar.service";
import { UrlStateService } from "../../../services/internal-services/url-state.service";
import { MapService } from "../../../services/internal-services/map.service";
import { ReportService } from "../../../services/internal-services/report.service";
import { FeatureFlagsService } from "../../../services/internal-services/feature-flags.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { GeoJSONDatabaseService } from "../../../services/internal-services/geojson-database.service";

import CoreMap from "@vision/core-map-api/src/CoreMap";
import { catchError, map } from "rxjs/operators";
import { LayerUtilsService } from "../../../services/internal-services/layer-utils.service";
import { ITimelineFilter } from "../../../interfaces/i-timeline-filter";
import { FilterProjectService } from "../../../services/internal-services/filter-project.service";
import { TypeFilter } from "../../../model/type-filter";
import { VIEWER_SERVICE } from "../../../services/internal-services/viewer-services/inject-base-viewer.service";

@Directive()
export abstract class BaseViewerComponent implements OnInit, OnDestroy, AfterViewInit {
  protected destroyRef = inject(DestroyRef);

  protected layerService = inject(LayerService);

  protected loadingService = inject(LoadingService);

  protected viewerService = inject(VIEWER_SERVICE);

  protected viewerConfigurationService = inject(ViewerConfigurationService);

  protected viewerErrorCommunicatorService = inject(ViewerErrorCommunicatorService);

  protected mapOverlayService = inject(MapOverlaysService);

  protected measurementToolService = inject(MeasurementToolService);

  protected dialog = inject(MatDialog);

  protected galleryService = inject(ImageGalleryService);

  protected _snackBar = inject(MatSnackBar);

  protected featuresSelectedService = inject(FeaturesSelectedService);

  protected toolbarService = inject(ToolbarService);

  protected urlStateService = inject(UrlStateService);

  protected mapService = inject(MapService);

  protected reportService = inject(ReportService);

  protected featureFlagsService = inject(FeatureFlagsService);

  protected geoJSONDatabaseService = inject(GeoJSONDatabaseService);

  private injector = inject(Injector);

  map: any;

  mapId: string;

  loadingMap = false;

  disabledMap = false;

  selectedImage: any;

  ngOnInit() {
    this.mapId = `map${this.mapService.getMapIndex()}`;
    this.viewerConfigurationService.urlParams$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((urlParams) => {
      this.handleUrlParameters(urlParams);
    });
    this.viewerConfigurationService.setDefaultLocation$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((setDefaultLocation) => {
        if (setDefaultLocation) {
          this.viewerService.setDefaultLocation();
        }
      });

    this.viewerConfigurationService.readNotifications$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((readNotifications) => {
        if (readNotifications) {
          this.viewerService.setReadNotifications(readNotifications);
          // i dont know why we save this here, maybe we only need if we have notifications read, to save this configuration
          if (readNotifications?.length) {
            // we call this sometimes before initialize map
            this.viewerService.saveUserConfiguration(this.viewerService.getLayersToSaveConfiguration());
          }
        }
      });

    this.viewerConfigurationService.fitToWKT$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((polygonWKT) => {
      if (polygonWKT) {
        const wktFeature = this.layerService.createFeatureWkt(
          this.mapService.getCoreMap(),
          polygonWKT,
          ViewerConstants.PROJECTIONS.WGS84,
          {},
        );
        this.mapService.getCoreMap().addTemporaryFeatureToDrawingLayer(wktFeature);
        this.mapService.fitToWktPolygon(polygonWKT, ViewerConstants.PROJECTIONS.WGS84);
      }
    });
  }

  ngAfterViewInit() {
    const isGalleryOpen = localStorage.getItem("isGalleryOpen");

    const viewerConf = this.viewerConfigurationService.getViewerConfiguration();

    if (viewerConf && this.viewerConfigurationService.isValid()) {
      const customOptions = this.customOptions();
      if (customOptions) {
        viewerConf.options = { ...viewerConf.options, ...customOptions };
      }

      const stateParams = this.urlStateService.getStateParam(this.mapId);
      this.initializeMapView(
        viewerConf.token,
        viewerConf.appName,
        viewerConf.authType,
        this.mapId,
        viewerConf.instanceId,
        stateParams,
        viewerConf.options,
      );
    }

    this.measurementToolService.groundElevationPopup$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((popup: IPopup) => {
        this.mapOverlayService.showGroundElevationPopup(popup);
      });

    this.loadingService.showGallery$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((loadId: string) => {
      if (loadId === "gallery" && !!isGalleryOpen && !this.dialog.openDialogs.length) {
        this.openImageGalleryReload();
      }
    });

    this.viewerErrorCommunicatorService.viewerErrorSubject$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((errorMessage: string) => {
        if (errorMessage) {
          this._snackBar.open(errorMessage, null, { duration: 7000 });
        }
      });

    this.featuresSelectedService.featuresClicked$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((featuresSelected) => {
        this.handleFeaturesClicked(featuresSelected);
      });

    this.viewerService.pointInfoPopup$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pointInfoPopup) => {
      if (pointInfoPopup) {
        this.mapOverlayService.showPointInfoPopup(pointInfoPopup, this.injector);
      } else {
        this.mapOverlayService.removePointInfoPopups();
      }
    });

    this.viewerService.firstLoad$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((finished) => {
      if (finished) {
        this.handleFirstLoadEvent();
      }
    });
  }

  ngOnDestroy() {
    this.viewerConfigurationService.initializedMap(false);

    if (this.map) {
      this.map.dispose();
    }
  }

  /**
   * Configures map options based on the provided configuration object.
   * This method is abstract and should be implemented in child classes to define specific behavior for setting up the map configuration.
   *
   * @param config Configuration object containing the details to customize the map. The structure of the configuration will depend on specific implementation.
   * @return void
   */
  abstract configureMapOptions(config: any): void;

  /**
   * After first load event
   *
   * */
  abstract handleFirstLoadEvent();

  /**
   * Launch update size on map object
   * This should be launch after enable and disable dual view
   * to repaint after change map container div
   */
  updateSizeMap() {
    const coreMap = this.mapService.getCoreMap();
    if (coreMap) {
      setTimeout(() => {
        if (coreMap.map) {
          coreMap.map.updateSize();
        }
      }, 200);
    }
  }

  enableContextMenu() {
    this.viewerService.enableContextMenu();
  }

  abstract openImageGalleryReload(): void;

  abstract openImageGalleryDialog(mapCenter: number[] | string[], images: any[], location: string): void;

  abstract calculateInitLocation(config: any, urlLocation: IUrlParams): Observable<IUrlParams>;

  abstract preprocessLayers(config: any): Viewerlayer[];

  abstract finishInitialization(config: any): void;

  abstract customOptions(): any;

  protected handleFeaturesClicked(featuresSelected: ISelectedFeatures) {
    if (!featuresSelected?.features?.length) return;

    if (
      !(
        this.viewerService.getMeasurementTools().find((tool) => tool.active === true) ||
        this.toolbarService.getEnableTool() === toolbarOptions.EXPORT_GEOTIFF ||
        this.toolbarService.getEnableTool() === toolbarOptions.CUSTOM_ELEMENTS_DETECTION
      ) &&
      featuresSelected
    ) {
      const features: any = featuresSelected.features.filter(
        (feat) =>
          feat?.layerName !== "vMeasurement" &&
          feat.properties.type !== "calibration" &&
          feat.properties.type !== "selection",
      );
      features.map((f) => (f.point = featuresSelected.point.coordinates));
      if (features && features.length !== 0) {
        if (
          this.viewerService
            .getUserLayers()
            .find((l) => l.name === features[0].properties.layerRef || l.name === features[0].layerName)
        ) {
          this.viewerService.setSelectedFeatures(features);
        } else {
          this.viewerService.showLocationInfo(featuresSelected.point.coordinates, features);
        }
      }
    }
  }

  /**
   * Check if this map is the first map created on the container
   * With this we can know if this map is created for dual view purposes
   */
  isMasterMap(): boolean {
    return this.mapId === "map1";
  }

  /**
   * Handle changes in the url parameters
   * This only be called if we change the url parameters manually
   * @param urlParams
   */
  handleUrlParameters(urlParams: IUrlParams) {
    if (!urlParams || !this.mapService.isMapInitialized()) {
      return;
    }
    // if we have new address we show location popup in this new address
    const savedUrlParameters = this.urlStateService.getSavedUrlParams();
    const center = this.viewerService.getLocation();

    if (urlParams.address && savedUrlParameters.address !== urlParams.address) {
      this.handleSearchAddressInUrl(urlParams.address, this.isMasterMap());
    } else if (
      // if we have latlng different than current we set center to this new location
      center &&
      ((urlParams.latitude && urlParams.latitude !== center[1]) ||
        (urlParams.longitude && urlParams.longitude !== center[0]) ||
        urlParams.latitude !== savedUrlParameters.latitude ||
        urlParams.longitude !== savedUrlParameters.longitude)
    ) {
      this.galleryService.setDisplayGallery(false);
      this.galleryService.setDockOpen(false);
      this.galleryService.closeFullscreen();
      this.mapService.setCenter(
        [urlParams.longitude ? urlParams.longitude : center[0], urlParams.latitude ? urlParams.latitude : center[1]],
        ViewerConstants.PROJECTIONS.WGS84,
      );
      this.viewerService.updateLocationInfo();
    } else if (!urlParams.address && (this.viewerService.getInfoPopup() || this.viewerService.getInfoProperties())) {
      this.viewerService.closeAddressPopup();
    }
    // update zoom value
    const zoom = this.viewerService.getZoom();
    if (zoom && urlParams.zoom && zoom !== urlParams.zoom) {
      this.viewerService.setZoom(urlParams.zoom);
    }

    this.urlStateService.mergeUrlParams(urlParams);
  }

  /**
   * Search location for this address and show info popup
   * @param address
   * @param center
   */
  handleSearchAddressInUrl(address: string, center: boolean = true) {
    this.galleryService.setDisplayGallery(false);
    this.galleryService.setDockOpen(false);
    this.galleryService.closeFullscreen();
    if (!address) {
      return;
    }
    GeoService.geocoding(address).subscribe((result: any[]) => {
      const searchedLocation = [result[0].geometry.location.lng(), result[0].geometry.location.lat()];
      if (center) {
        this.map.setCenter(searchedLocation, ViewerConstants.PROJECTIONS.WGS84);
      }
      this.showLocationInfo(searchedLocation);
    });
  }

  showLocationInfo(searchedInfo) {
    const featureMercator = GeoService.pointToMercator(searchedInfo);
    const pixel = this.viewerService.coordinatesToPixel([featureMercator.geometry.coordinates]);
    const features = this.mapService.getCoreMap().getFeaturesClicked(pixel[0], featureMercator.geometry.coordinates);
    this.viewerService.showLocationInfo([searchedInfo[0], searchedInfo[1]], features.length ? features : null);
  }

  /**
   * Load layers in angular side
   * Get user layers and insert in map
   * @param layers
   */
  loadCommonLayers(layers: Viewerlayer[]): void {
    this.viewerService.addCollectionLayers(this.layerService.getCollectionLayers(layers));
    this.viewerService.addOverlayLayers(
      this.layerService
        .getOverlayLayers(layers)
        .filter((layer) => layer.layerType !== ViewerConstants.LAYER_TYPE.COLLECTIONLAYER),
    );
    this.viewerService.addBaseLayers(this.layerService.getBaseLayers(layers));

    const viewerConfig = this.viewerConfigurationService.getViewerConfiguration();
    // get data from index data base for user layers
    if (viewerConfig.userId) {
      this.geoJSONDatabaseService.getUserLayers(viewerConfig.userId).subscribe((userLayers) => {
        userLayers.sort((a, b) => b.order - a.order);
        if (userLayers && userLayers.length) {
          // create viewer layer definition from feature collections and add to map
          this.viewerService.addUserLayers(userLayers);
        }
      });
    }
  }

  /**
   * Get list of layers to send to save configuration
   */
  getLayersToSaveConfiguration(): Viewerlayer[] {
    return this.viewerService.getCollections().concat(this.viewerService.getOverlayLayers());
  }

  /**
   * Initialize map view with user token and instance
   * @param appName
   * @param authType
   * @param {number} instance
   * @param {string} userToken token from auth
   * @param {string} mapId
   * @param stateParams
   * @param options Optional map options to merge with defaults
   */
  initializeMapView(
    userToken: string,
    appName: string,
    authType: string,
    mapId: string,
    instance: number,
    stateParams: any,
    options?: any,
  ): void {
    this.loadingMap = true;
    this.loadingService.showLoadingComponent("map");

    // Default CoreMap options
    const defaultOptions = {
      target: mapId,
      token: userToken,
      authType: authType,
      appName: appName,
      instanceId: instance,
      populateCoverage: false,
      enableContextMenu: true,
      enableTileSize: true,
      init: false,
      aois: true,
      mode: stateParams?.mode,
      orientation: stateParams?.orientation,
      scale: true,
      scaleTarget: `scale-container-${this.mapService.getMapIndex()}`,
      status: true,
      callback: this.initializeCallback.bind(this),
    };

    // Merge user-provided options (if any) with defaults (user options override defaults)
    const mergedOptions = { ...defaultOptions, ...(options || {}) };

    this.map = new CoreMap(mergedOptions);
  }

  initializeCallback(response: any): void {
    try {
      // hide loading map indicators
      this.loadingMap = false;
      this.loadingService.hideLoadingComponent("map");

      if (
        !response ||
        !response.ready ||
        !response.initConfig ||
        (!response.initConfig.layers && response.initConfig.layers.length === 0)
      ) {
        throw new Error("Response from init service not expected");
      } else {
        this.viewerConfigurationService.updateInstanceId(response.initConfig.instanceId);
        this.viewerService.setInstanceId(response.initConfig.instanceId);
        // get url params
        let urlParams = this.urlStateService.getParamsFromURL();

        this.featureFlagsService.setFlags(response.initConfig?.features || {});
        // process config data, layers and location
        this.preprocessConfig(response.initConfig, urlParams)
          .pipe(
            map((res) => {
              const config = res[0];
              const urlLocation = res[1];
              response.initConfig = config;
              urlParams = urlLocation;
            }),
            map(() => {
              // now initialize map with the layer config
              this.map.initialize(response.initConfig).then(() => {
                this.configureDualView();
                this.viewerService.setCoreMap(this.map);
                this.viewerService.setApiService(this.map.getApiService());

                this.handleInitState();

                // load layers in viewercomponents
                this.loadLayers(response.initConfig.layers);

                // configure tools and features
                this.configureMapOptions(response.initConfig);

                this.finishInitialization(response.initConfig);
              });
            }),
            catchError((e) => {
              console.log("Error on map initialize: " + e.message);
              throw new Error("Error reprocessing init data");
            }),
          )
          .subscribe();
        // if we have location data from url we turn on all the layers
      }
    } catch (e) {
      this.viewerErrorCommunicatorService.setViewerError("Cannot get data for this instance");
      // here we should load the map with openstreetmap
    }
  }

  /**
   * Initializes the state of the map and viewer based on the current configuration
   * and URL state parameters. It handles discrete mode setup, changes viewer mode,
   * manages layer visibility, and processes any existing state parameters.
   *
   * @return {void} This method does not return a value.
   */
  handleInitState() {
    if (this.map.isDiscreteEnabled()) {
      this.mapService.setDiscreteOrientation(this.map.getOrientation());

      this.viewerService.onChangeModeView({ mode: ViewerConstants.COLLECTION_MODES.OBLIQUE });
      this.map.changePropertyFootprintLayerVisibility(false);
      // Optional chaining guards against "reading 'getView'" when handleInitState
      // races with map initialisation/destruction.
      const discreteInfo = this.map.olMap?.getView()?.getProjection()?.discreteInfo;
      if (discreteInfo) {
        this.viewerService.loadDiscreteFinished({ imageMetadata: discreteInfo });
      }
    }

    this.viewerService.orientationSelected = this.map.getOrientation();

    const state = this.urlStateService.getStateParam(this.mapService.getMapId());
    if (state) {
      this.viewerService.handleState(state);
    }
  }

  processUrlLocation(location: IUrlParams, urlLocation: IUrlParams, config): any {
    if ((!urlLocation.latitude || !urlLocation.longitude) && config.lastLocation) {
      // get location data from config
      const mapCenter = GeoService.wktToGeoJSONPoint(config.lastLocation, false).coordinates;
      const lat = mapCenter[1];
      const long = mapCenter[0];
      location.latitude = lat;
      location.longitude = long;

      if (!location.zoom && config.lastZoom) {
        location.zoom = config.lastZoom;
      }
    }
    return location;
  }

  /**
   * Process response data before initialize map
   * @param config
   * @param params
   */
  preprocessConfig(config, params: IUrlParams): Observable<any> {
    return this.calculateInitLocation(config, params).pipe(
      map((location) => {
        if (location.latitude && location.longitude) {
          config.lastLocation = GeoService.pointToWkt([location.longitude, location.latitude]);
        }

        if (location.zoom) {
          config.lastZoom = location.zoom;
        }
        //update layer from url and apply in the processing
        // probably we have to run again layer after get the aoi layers

        config.filters = this.preprocessFilters(config);

        config.layers = this.preprocessLayers(config);

        return [config, location];
      }),
    );
  }

  preprocessFilters(config: any): any {
    const stateParam = this.urlStateService.getStateParam(this.mapService.getMapId());

    if (stateParam?.filters) {
      config.filters = stateParam?.filters;
    }

    //todo change this shit, i´m not proud of it...
    const multiLayer = config.layers?.find((l) => l.layerType === "VEXCEL_MULTI_LAYER");

    //process filters from config or url
    const filters = this.calculateFilter(config.filters, multiLayer);

    if (filters) {
      this.viewerService.setTimelineFilters(filters);
    }

    return config.filters;
  }

  /**
   * Update layers from state
   */
  updateLayerConfig(config: any, layers: Viewerlayer[]) {
    const stateParam = this.urlStateService.getStateParam(this.mapService.getMapId());

    this.setInitialAOI(stateParam, config, layers);

    // this.viewerService.handleState(stateParam);
    //Set
    this.setInitialSources(stateParam, config, layers);

    if (stateParam?.layers?.length) {
      LayerUtilsService.syncLayersConfiguration(layers, stateParam?.layers);
    }
  }

  /**
   *
   *
   * @param stateParam
   * @param config
   * @param layers
   */
  setInitialAOI(stateParam: any, config: any, layers: Viewerlayer[]) {
    let initialData = stateParam?.initialData || config?.initialData;
    const nir = stateParam?.nir;
    const mode = stateParam?.mode;
    const deprecatedAOIParam = this.getDeprecatedAOIValue(stateParam, config);

    let sl;

    if (!!initialData && !!initialData.layer && !!initialData.aoi) {
      sl = layers.find((l) => l.layer === initialData.layer);
      if (!sl) {
        sl = layers.find((l) => l.layers?.includes(initialData.layer));
      }
    } else if (!!deprecatedAOIParam) {
      initialData = { aoi: deprecatedAOIParam[1] };
      sl = layers.find((l) => l.id == deprecatedAOIParam[0]);
    }

    if (initialData && sl && initialData.aoi) {
      if (sl.layerType === ViewerConstants.LAYER_TYPE.MULTILAYER) {
        sl.collection = initialData.aoi;
        sl.bands = nir && mode !== ViewerConstants.COLLECTION_MODES.OBLIQUE.name ? ViewerConstants.BANDS.IRG : null;
      } else {
        sl.initialData = { parentAoi: initialData.parentAoi, aoi: initialData.aoi };
      }
      this.viewerService.setInitAoi(initialData.aoi);
    }
  }

  /**
   * Sets the initial sources for the provided layers based on the given state and configuration parameters.
   *
   * @param {any} stateParam - The state parameter, potentially including*/
  setInitialSources(stateParam: any, config: any, layers: Viewerlayer[]) {
    let initialFilters = stateParam?.filters || config?.filters;

    // If there´s no layerTypes on the filter configuration do nothing
    if (!initialFilters?.layerTypes?.length) {
      return;
    }

    const visibleSources = initialFilters.layerTypes
      .filter((f) => f.checked)
      .map((f) => f.value.split(","))
      .flat();

    if (visibleSources) {
      layers
        .filter((l) => l.layerType === ViewerConstants.LAYER_TYPE.MULTILAYER)
        .map((l) => (l.layers = l.layers.filter((l) => visibleSources.find((s) => s === l))));
    }
  }

  /**
   * Get deprecated aoi value from state
   * @deprecated
   */
  getDeprecatedAOIValue(stateParam, config) {
    let aoi = stateParam?.aoi || config.aoi;

    //REGEX for check the selectedAOI it´s legacy used
    const regex = /^\d+-[a-zA-Z0-9_ -]+$/;

    //TODO this part should be removed in the future
    if (aoi && regex.test(aoi)) {
      // in order to make compatibility with current DB savings
      // Get the first part that correspond to the
      return aoi.split(/-(.+)/);
    }

    return;
  }

  formatFilterName(filterName: string) {
    return filterName
      .split("-")
      .map((parte, index) =>
        index === 0 ? parte.charAt(0).toUpperCase() + parte.slice(1) : parte.toUpperCase().charAt(0) + parte.slice(1),
      )
      .join(" ");
  }

  calculateFilter(configFilters: any, multiLayer: any): ITimelineFilter {
    //todo remove this when deprecated collections
    let filter: ITimelineFilter;
    if (multiLayer) {
      // Create the preConfigured filters
      filter = FilterProjectService.emptyCollectionFilter();

      multiLayer.layers.forEach((l: string) => {
        if (!filter.layerTypes.find((lt) => lt.value.includes(l))) {
          filter.layerTypes.push(TypeFilter(this.formatFilterName(l), l, "", l, ""));
        }
      });

      // Remove filters that are not needed.
      filter.layerTypes = filter?.layerTypes
        ?.map((lt: any) => {
          const filterValues = lt.value.split(",");
          const resultFilters = filterValues.filter((fv: any) => multiLayer.layers.includes(fv));
          lt.value = resultFilters.join(",");
          return lt;
        })
        .filter((lt: any) => lt.value);
    } else {
      filter = FilterProjectService.emptyProjectsFilter();
    }

    if (configFilters) {
      return FilterProjectService.populateFilter(filter, configFilters);
    }

    return filter;
  }
  abstract configureDualView(): void;

  abstract loadLayers(layers: any[]): void;
}
